import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCar, FaCarSide, FaBuilding, FaExclamationTriangle, FaClock, FaCheckCircle, FaExchangeAlt, FaUndo, FaSyncAlt } from 'react-icons/fa';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalBiler: 0,
    totalLaanebiler: 0,
    totalStationer: 0,
    aktiveFejlmeldinger: 0
  });
  const [syneBiler, setSyneBiler] = useState([]);
  const [visibleSyneBiler, setVisibleSyneBiler] = useState(5);
  const [selectedSynCar, setSelectedSynCar] = useState(null);
  const [showSynModal, setShowSynModal] = useState(false);
  const [refreshingSyn, setRefreshingSyn] = useState(false);
  const [synError, setSynError] = useState('');
  const [synToast, setSynToast] = useState(null);
  const [udlaanteLaanebiler, setUdlaanteLaanebiler] = useState([]);
  const [udlaanteCount, setUdlaanteCount] = useState(0);
  const [weeklyAfspadseringer, setWeeklyAfspadseringer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lowAvailabilityTypes, setLowAvailabilityTypes] = useState([]);
  const [selectedLaanebil, setSelectedLaanebil] = useState(null);
  const [showLoanerActions, setShowLoanerActions] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [stationer, setStationer] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [manualEmails, setManualEmails] = useState('');
  const [workshopCars, setWorkshopCars] = useState([]);
  const [moveData, setMoveData] = useState({ car_id: '', station_id: '', workshop_car_id: '', bemaerkninger: '' });
  const [supplementaryLoading, setSupplementaryLoading] = useState(false);
  const [supplementaryError, setSupplementaryError] = useState('');
  const [hasLoadedSupplementary, setHasLoadedSupplementary] = useState(false);
  const [workshopLoading, setWorkshopLoading] = useState(false);
  const [moveError, setMoveError] = useState('');
  const [returnError, setReturnError] = useState('');
  const [activeServiceSessions, setActiveServiceSessions] = useState([]);
  const synToastTimerRef = useRef(null);

  const showSynToast = useCallback((message, type = 'info') => {
    setSynToast({ message, type });
    if (synToastTimerRef.current) {
      clearTimeout(synToastTimerRef.current);
    }
    synToastTimerRef.current = setTimeout(() => {
      setSynToast(null);
      synToastTimerRef.current = null;
    }, 4000);
  }, []);

  useEffect(() => {
    return () => {
      if (synToastTimerRef.current) {
        clearTimeout(synToastTimerRef.current);
        synToastTimerRef.current = null;
      }
    };
  }, []);

  const formatSynToastDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('da-DK');
  };

  const loadActiveServiceSessions = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      const sessions = [];
      const maxAge = 7 * 24 * 60 * 60 * 1000;
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i);
        if (!key || !key.startsWith('serviceSession:')) continue;
        const raw = window.localStorage.getItem(key);
        if (!raw) continue;
        let parsed;
        try {
          parsed = JSON.parse(raw);
        } catch (_) {
          continue;
        }
        if (!parsed || typeof parsed !== 'object') continue;
        const timestamp = typeof parsed.ts === 'number'
          ? parsed.ts
          : (typeof parsed?.meta?.updatedAt === 'number' ? parsed.meta.updatedAt : null);
        if (!timestamp) continue;
        if (Date.now() - timestamp > maxAge) {
          window.localStorage.removeItem(key);
          continue;
        }
        const meta = parsed.meta || {};
        const keyParts = key.split(':');
        const bilIdFromKey = keyParts.length > 1 ? keyParts[1] : '';
        const categoriesPart = keyParts.length > 2 ? keyParts[2] : '';
        const categoryIds = Array.isArray(meta.categoryIds)
          ? meta.categoryIds
          : (typeof meta.categoryIds === 'string' && meta.categoryIds.length
              ? meta.categoryIds.split(',').map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n))
              : (categoriesPart
                  ? categoriesPart.split(',').map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n))
                  : []));
        sessions.push({
          key,
          updatedAt: timestamp,
          bilId: meta.bilId || bilIdFromKey,
          interntNummer: meta.interntNummer || meta.internt_nummer || '',
          categoryIds,
          categoryNames: Array.isArray(meta.categoryNames) ? meta.categoryNames : [],
          kilometerstand: parsed.kilometerstand ?? meta.kilometerstand ?? '',
          mechanicSignature: parsed.mechanicSignature ?? meta.mechanicSignature ?? ''
        });
      }
      sessions.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
      setActiveServiceSessions(sessions);
    } catch (error) {
      console.error('Fejl ved hentning af aktive service-kladder:', error);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    loadActiveServiceSessions();
    if (typeof window === 'undefined') return () => {};
    const handleStorage = (event) => {
      if (event?.key && event.key.startsWith('serviceSession:')) {
        loadActiveServiceSessions();
      }
    };
    const handleFocus = () => loadActiveServiceSessions();
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
    };
  }, [loadActiveServiceSessions]);

  const fetchDashboardData = async () => {
    try {
      // Hent statistikker
      const statsResponse = await fetch('/api/dashboard/stats');
      const statsData = await statsResponse.json();
      setStats(statsData);

      // Hent biler der skal synes
        const syneResponse = await fetch('/api/dashboard/syne-biler');
      const syneData = await syneResponse.json();
      setSyneBiler(syneData);
        setVisibleSyneBiler(5);

      // Hent udlånte lånebiler
      const laanebilerResponse = await fetch('/api/dashboard/udlaante-laanebiler');
      const laanebilerData = await laanebilerResponse.json();
      setUdlaanteLaanebiler(laanebilerData);
      setUdlaanteCount(Array.isArray(laanebilerData) ? laanebilerData.length : 0);

      try {
        const weeklyRes = await fetch('/api/afspadsering/this-week');
        if (weeklyRes.ok) {
          const weeklyData = await weeklyRes.json();
          setWeeklyAfspadseringer(Array.isArray(weeklyData) ? weeklyData : []);
        } else {
          setWeeklyAfspadseringer([]);
        }
      } catch (_) {
        setWeeklyAfspadseringer([]);
      }

      // Hent alle lånebiler for at udlede lav beholdning pr. vogn type
      try {
        const allLoanersRes = await fetch('/api/laanebiler');
        const allLoaners = await allLoanersRes.json();
        if (Array.isArray(allLoaners)) {
          const availableByType = {};
          const totalByType = {};
          for (const car of allLoaners) {
            const type = car.vogn_type || 'Ukendt type';
            totalByType[type] = (totalByType[type] || 0) + 1;
            if (car.status !== 'Udlånt') {
              availableByType[type] = (availableByType[type] || 0) + 1;
            }
          }
          const low = Object.keys(availableByType).filter(t => availableByType[t] === 1 && (totalByType[t] || 0) > 1);
          setLowAvailabilityTypes(low);
        } else {
          setLowAvailabilityTypes([]);
        }
      } catch (_) {
        setLowAvailabilityTypes([]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      loadActiveServiceSessions();
    }
  };

  const formatAfspadseringRange = (start, end, startTime, endTime, startDayEndTime) => {
    if (!start && !end) return '';
    const options = { day: 'numeric', month: 'numeric' };
    const startText = start ? new Date(start).toLocaleDateString('da-DK', options) : '';
    const endText = end ? new Date(end).toLocaleDateString('da-DK', options) : '';
    const cleanStartTime = startTime ? String(startTime).slice(0, 5) : '';
    const cleanEndTime = endTime ? String(endTime).slice(0, 5) : '';
    const cleanStartDayEndTime = startDayEndTime ? String(startDayEndTime).slice(0, 5) : '';
    const sameDay = start && end && String(start).slice(0, 10) === String(end).slice(0, 10);
    if (sameDay) {
      if (cleanStartTime && cleanEndTime) return `${startText} kl. ${cleanStartTime}-${cleanEndTime}`;
      if (cleanStartTime) return `${startText} fra kl. ${cleanStartTime}`;
      if (cleanEndTime) return `${startText} til kl. ${cleanEndTime}`;
      return startText || endText;
    }
    if (startText && endText) {
      const startSuffix = cleanStartTime && cleanStartDayEndTime
        ? ` kl. ${cleanStartTime}-${cleanStartDayEndTime}`
        : cleanStartTime
          ? ` kl. ${cleanStartTime}`
          : '';
      const endSuffix = cleanEndTime ? ` kl. ${cleanEndTime}` : '';
      return `${startText}${startSuffix} - ${endText}${endSuffix}`;
    }
    return startText || endText;
  };

  const handleSessionResume = useCallback((session) => {
    if (!session || !session.bilId) {
      alert('Kan ikke åbne denne service – mangler biloplysninger.');
      return;
    }
    const categoriesParam = Array.isArray(session.categoryIds) && session.categoryIds.length
      ? session.categoryIds.join(',')
      : '';
    if (!categoriesParam) {
      alert('Kan ikke åbne denne service – mangler kategorioplysninger.');
      return;
    }
    const interntParam = session.interntNummer ? encodeURIComponent(session.interntNummer) : '';
    navigate(`/services/details?bil_id=${session.bilId}&internt_nummer=${interntParam}&categories=${categoriesParam}`);
  }, [navigate]);

  const handleSessionDelete = useCallback((sessionKey) => {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(sessionKey);
    loadActiveServiceSessions();
  }, [loadActiveServiceSessions]);

  const loadSupplementaryData = async () => {
    if (hasLoadedSupplementary) return;
    setSupplementaryLoading(true);
    setSupplementaryError('');
    try {
      const stationRes = await fetch('/api/stationer');
      if (!stationRes.ok) {
        throw new Error('Kunne ikke hente stationer');
      }
      const stationData = await stationRes.json();
      setStationer(Array.isArray(stationData) ? stationData : []);

      const token = localStorage.getItem('token');
      const usersRes = await fetch('/api/indstillinger/brugere', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(Array.isArray(usersData) ? usersData : []);
      } else if (usersRes.status === 401) {
        setUsers([]);
      } else {
        throw new Error('Kunne ikke hente brugere');
      }

      setHasLoadedSupplementary(true);
    } catch (error) {
      console.error('Fejl ved hentning af data til flytning af lånebil:', error);
      setSupplementaryError('Kunne ikke hente nødvendige data. Prøv igen senere.');
    } finally {
      setSupplementaryLoading(false);
    }
  };

  const fetchWorkshopCarsForStation = async (stationId) => {
    if (!stationId) {
      setWorkshopCars([]);
      return;
    }
    setWorkshopLoading(true);
    try {
      const res = await fetch(`/api/laanebiler/station/${stationId}/cars`);
      if (!res.ok) {
        throw new Error('Kunne ikke hente værkstedsbiler');
      }
      const data = await res.json();
      setWorkshopCars(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Fejl ved hentning af værkstedsbiler:', error);
      setWorkshopCars([]);
    } finally {
      setWorkshopLoading(false);
    }
  };

  const handleLoanerCardClick = (laanebil) => {
    setSelectedLaanebil(laanebil);
    setShowLoanerActions(true);
    setMoveError('');
    setReturnError('');
  };

  const handleCloseLoanerActions = () => {
    setShowLoanerActions(false);
    setSelectedLaanebil(null);
    setMoveError('');
    setReturnError('');
  };

  const handleOpenMoveModal = async () => {
    if (!selectedLaanebil) return;
    await loadSupplementaryData();
    setMoveError('');
    const loanerId = selectedLaanebil.id ?? selectedLaanebil.laanebil_id ?? selectedLaanebil.car_id;
    setMoveData({
      car_id: loanerId ? String(loanerId) : '',
      station_id: '',
      workshop_car_id: '',
      bemaerkninger: ''
    });
    setSelectedRecipients([]);
    setSelectedUserId('');
    setManualEmails('');
    setWorkshopCars([]);
    setShowLoanerActions(false);
    setShowMoveModal(true);
  };

  const handleCloseMoveModal = () => {
    setShowMoveModal(false);
    setMoveError('');
    setMoveData({ car_id: '', station_id: '', workshop_car_id: '', bemaerkninger: '' });
    setSelectedRecipients([]);
    setSelectedUserId('');
    setManualEmails('');
    setWorkshopCars([]);
    if (selectedLaanebil) {
      setShowLoanerActions(true);
    }
  };

  const handleMoveSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLaanebil) {
      setMoveError('Ingen lånebil valgt.');
      return;
    }
    const loanerId = selectedLaanebil.id ?? selectedLaanebil.laanebil_id ?? selectedLaanebil.car_id;
    if (!loanerId) {
      setMoveError('Kunne ikke identificere lånebilen.');
      return;
    }
    try {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/laanebiler/move';
      form.target = '_blank';

      const payload = { ...moveData, car_id: loanerId ? String(loanerId) : '' };
      Object.entries(payload).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

        const token = localStorage.getItem('token');
        if (token) {
          const tokenInput = document.createElement('input');
          tokenInput.type = 'hidden';
          tokenInput.name = 'token';
          tokenInput.value = token;
          form.appendChild(tokenInput);
        }

      if (selectedRecipients.length > 0) {
        selectedRecipients.forEach((id) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'recipients';
          input.value = id;
          form.appendChild(input);
        });
      }

      if (manualEmails && manualEmails.trim().length > 0) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'emails';
        input.value = manualEmails;
        form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

      setShowMoveModal(false);
      setMoveData({ car_id: '', station_id: '', workshop_car_id: '', bemaerkninger: '' });
      setSelectedRecipients([]);
      setSelectedUserId('');
      setManualEmails('');
      setWorkshopCars([]);
      setSelectedLaanebil(null);
      setTimeout(() => {
        fetchDashboardData();
      }, 1000);
    } catch (error) {
      console.error('Error moving laanebil:', error);
      setMoveError('Kunne ikke flytte lånebilen. Prøv igen.');
    }
  };

  const handleOpenReturnModal = () => {
    if (!selectedLaanebil) return;
    setReturnError('');
    setShowLoanerActions(false);
    setShowReturnModal(true);
  };

  const handleCloseReturnModal = () => {
    setShowReturnModal(false);
    setReturnError('');
    if (selectedLaanebil) {
      setShowLoanerActions(true);
    }
  };

  const handleReturnConfirm = () => {
    if (!selectedLaanebil) {
      setReturnError('Ingen lånebil valgt.');
      return;
    }
    const loanerId = selectedLaanebil.id ?? selectedLaanebil.laanebil_id ?? selectedLaanebil.car_id;
    if (!loanerId) {
      setReturnError('Kunne ikke identificere lånebilen.');
      return;
    }
    try {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/laanebiler/return';
      form.target = '_blank';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'car_id';
      input.value = loanerId;
      form.appendChild(input);

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

      setShowReturnModal(false);
      setSelectedLaanebil(null);
      setTimeout(() => {
        fetchDashboardData();
      }, 1000);
    } catch (error) {
      console.error('Error returning laanebil:', error);
      setReturnError('Kunne ikke returnere lånebilen. Prøv igen.');
    }
  };

  const handleRemoveRecipient = (id) => {
    setSelectedRecipients((prev) => prev.filter((recipientId) => recipientId !== id));
  };

  const statCards = [
    {
      title: 'Totale Biler',
      value: stats.totalBiler,
      icon: FaCar,
      color: 'bg-blue-500',
      link: '/biler'
    },
    {
      title: 'Lånebiler',
      value: stats.totalLaanebiler,
      icon: FaCarSide,
      color: 'bg-green-500',
      link: '/laanebiler'
    },
    {
      title: 'Stationer',
      value: stats.totalStationer,
      icon: FaBuilding,
      color: 'bg-purple-500',
      link: '/stationer'
    },
    {
      title: 'Aktive Fejlmeldinger',
      value: stats.aktiveFejlmeldinger,
      icon: FaExclamationTriangle,
      color: 'bg-red-500',
      link: '/fejlmeldinger'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Adaptiv velkomst */}
      <AdaptiveWelcome stats={stats} udlaanteCount={udlaanteCount} lowTypes={lowAvailabilityTypes} />

      <ActiveServiceSessions
        sessions={activeServiceSessions}
        onResume={handleSessionResume}
        onDelete={handleSessionDelete}
      />

      {/* Statistik kort */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate(card.link)}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{card.title}</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{card.value}</p>
              </div>
              <div className={`p-3 rounded-full ${card.color}`}>
                <card.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Biler der skal synes */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200 dark-border-gray-800 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center">
                  <div className="flex items-center gap-2">
                    <FaClock className="h-5 w-5 text-orange-500" />
                    <span>Biler der skal synes (næste 2 mdr.)</span>
                  </div>
              </h2>
              <button
                type="button"
                className="text-sm font-semibold text-falck-blue hover:underline inline-flex items-center gap-2"
                onClick={async () => {
                  if (refreshingSyn || syneBiler.length === 0) return;
                  setRefreshingSyn(true);
                  setSynError('');
                  try {
                    const updatedDates = [];
                    for (const bil of syneBiler) {
                      if (!bil.nummerplade) continue;
                      const res = await fetch('/api/biler/dmr-lookup', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ nummerplade: bil.nummerplade, bil_id: bil.id })
                      });
                      if (res.ok) {
                        const data = await res.json().catch(() => ({}));
                        if (data?.data?.mot_dato) {
                          updatedDates.push(data.data.mot_dato);
                        }
                      }
                    }
                    await fetchDashboardData();
                    if (updatedDates.length > 0) {
                      const latest = updatedDates[updatedDates.length - 1];
                      showSynToast(`Synsdato opdateret: ${formatSynToastDate(latest)}`, 'success');
                    } else {
                      showSynToast('Ingen synsdato modtaget fra Motor API.', 'info');
                    }
                  } catch (error) {
                    console.error('Kunne ikke opdatere synsdatoer:', error);
                    setSynError('Kunne ikke opdatere alle synsdatoer. Prøv igen.');
                    showSynToast('Kunne ikke opdatere synsdatoer. Prøv igen.', 'error');
                  } finally {
                    setRefreshingSyn(false);
                  }
                }}
                disabled={refreshingSyn || syneBiler.length === 0}
              >
                <FaSyncAlt className={`h-4 w-4 ${refreshingSyn ? 'animate-spin' : ''}`} />
                Opdatér alle
              </button>
            </div>
          <div className="p-6">
              {syneBiler.length > 0 ? (
                <div className="space-y-3">
                  {syneBiler.slice(0, visibleSyneBiler).map((bil) => (
                    <div 
                      key={bil.id} 
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                      onClick={() => {
                        setSelectedSynCar(bil);
                        setSynError('');
                        setShowSynModal(true);
                      }}
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{bil.internt_nummer}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{bil.vogn_type} - {bil.station_navn}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-orange-600">Synsdato: {bil.synsdato}</p>
                      </div>
                    </div>
                  ))}
                  {visibleSyneBiler < syneBiler.length && (
                    <div className="text-center mt-3">
                      <button
                        type="button"
                        onClick={() => setVisibleSyneBiler((prev) => Math.min(prev + 25, syneBiler.length))}
                        className="text-sm font-semibold text-falck-blue hover:underline"
                      >
                        +{Math.min(25, syneBiler.length - visibleSyneBiler)} flere biler
                      </button>
                    </div>
                  )}
                </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-300 text-center py-4">
                Ingen biler skal synes denne måned
              </p>
            )}
          </div>
        </div>

        {/* Udlånte lånebiler */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-800">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center">
              <FaCarSide className="h-5 w-5 mr-2 text-green-500" />
              Aktuelt udlånte lånebiler
            </h2>
          </div>
          <div className="p-6">
            {udlaanteLaanebiler.length > 0 ? (
              <div className="space-y-3">
                {udlaanteLaanebiler.slice(0, 5).map((bil) => {
                  const loanerLabel = [
                    bil.laanebil_internt_nummer || bil.internt_nummer,
                    bil.vogn_type
                  ].filter(Boolean).join(', ');
                  return (
                    <div 
                      key={bil.id} 
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                      onClick={() => handleLoanerCardClick(bil)}
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {loanerLabel || 'Lånebil'}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Erstatter: {bil.erstattet_bil_internt_nummer}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Station: {bil.station_navn}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Udlånt
                        </span>
                      </div>
                    </div>
                  );
                })}
                {udlaanteLaanebiler.length > 5 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-3">
                    +{udlaanteLaanebiler.length - 5} flere lånebiler
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-300 text-center py-4">
                Ingen lånebiler er aktuelt udlånt
              </p>
            )}
          </div>
        </div>
      </div>

      {weeklyAfspadseringer.length > 0 && (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
              <FaClock className="h-5 w-5 text-falck-blue" />
              Afspadsering denne uge
            </h2>
          </div>
          <div className="p-6 space-y-3">
            {weeklyAfspadseringer.map((entry) => (
              <div
                key={entry.id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50"
              >
                <div className="flex-1 min-w-0 space-y-1">
                  <p className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {entry.display_name || entry.name || entry.brugernavn || 'Medarbejder'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 break-words">
                    Mandskabsnr.: {entry.crew_number || '—'}
                  </p>
                  {entry.comment && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 break-words leading-snug">
                      Kommentar: {entry.comment}
                    </p>
                  )}
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-200 sm:text-right flex-shrink-0">
                  <p className="font-semibold break-words">
                    {formatAfspadseringRange(entry.start_date, entry.end_date, entry.start_time, entry.end_time, entry.start_day_end_time)}
                  </p>
                  {entry.days ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">{entry.days} dage</p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

        {showLoanerActions && selectedLaanebil && (
        <div
          className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={handleCloseLoanerActions}
        >
          <div
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg w-full max-w-md sm:max-w-lg mx-auto p-4 sm:p-6 space-y-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {selectedLaanebil.laanebil_internt_nummer || selectedLaanebil.internt_nummer || 'Lånebil'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {selectedLaanebil.vogn_type || 'Ukendt type'}
              </p>
            </div>
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
              <p>
                <span className="font-medium text-gray-800 dark:text-gray-100">Station:</span>{' '}
                {selectedLaanebil.station_navn || 'Ikke angivet'}
              </p>
              <p>
                <span className="font-medium text-gray-800 dark:text-gray-100">Erstatter:</span>{' '}
                {selectedLaanebil.erstattet_bil_internt_nummer || 'Ingen'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <button
                onClick={handleOpenMoveModal}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-falck-blue text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <FaExchangeAlt /> Flyt lånebil
              </button>
              <button
                onClick={handleOpenReturnModal}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <FaUndo /> Returner lånebil
              </button>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleCloseLoanerActions}
                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Luk
              </button>
            </div>
          </div>
        </div>
      )}

        {showSynModal && selectedSynCar && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 px-2" onClick={() => setShowSynModal(false)}>
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-xl p-6 relative" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setShowSynModal(false)}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
              <div className="space-y-3">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {selectedSynCar.internt_nummer}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{selectedSynCar.vogn_type}</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <span className="font-medium">Station:</span> {selectedSynCar.station_navn || 'Ukendt'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <span className="font-medium">Første registreringsdato:</span> {selectedSynCar.foerste_registreringsdato || 'Ukendt'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <span className="font-medium">Synsdato:</span> {selectedSynCar.synsdato || 'Ukendt'}
                </p>
              </div>
              {synError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2 mt-3">
                  {synError}
                </div>
              )}
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate(`/biler/profil/${selectedSynCar.id}`)}
                  className="flex-1 px-4 py-2 rounded-md border border-gray-300 text-gray-800 hover:bg-gray-100"
                >
                  Gå til bilprofil
                </button>
                <button
                  onClick={async () => {
                    if (refreshingSyn || !selectedSynCar.nummerplade) {
                      setSynError('Kunne ikke opdatere – mangler nummerplade.');
                      return;
                    }
                    try {
                      setRefreshingSyn(true);
                      setSynError('');
                      const res = await fetch('/api/biler/dmr-lookup', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ nummerplade: selectedSynCar.nummerplade, bil_id: selectedSynCar.id })
                      });
                      if (!res.ok) throw new Error('Fejl ved opdatering');
                      const data = await res.json();
                        if (data?.data) {
                          if (data.data.mot_dato) {
                            await fetchDashboardData();
                            setShowSynModal(false);
                            showSynToast(`Synsdato opdateret: ${formatSynToastDate(data.data.mot_dato)}`, 'success');
                          } else {
                            setSynError('Ingen synsdato modtaget fra Motor API.');
                            showSynToast('Ingen synsdato modtaget fra Motor API.', 'info');
                          }
                      } else {
                        setSynError('Ingen synsdato modtaget fra Motor API.');
                        showSynToast('Ingen synsdato modtaget fra Motor API.', 'info');
                      }
                    } catch (error) {
                      console.error('Kunne ikke opdatere synsdato:', error);
                      setSynError('Kunne ikke opdatere synsdato. Prøv igen.');
                      showSynToast('Kunne ikke opdatere synsdato. Prøv igen.', 'error');
                    } finally {
                      setRefreshingSyn(false);
                    }
                  }}
                  className="flex-1 px-4 py-2 rounded-md bg-falck-blue text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={refreshingSyn}
                >
                  {refreshingSyn ? 'Opdaterer...' : 'Opdater synsdato'}
                </button>
              </div>
            </div>
          </div>
        )}

      {synToast && (
        <div className="fixed top-4 right-4 z-50">
          <div
            className={`px-4 py-3 rounded-lg shadow-lg border text-sm ${
              synToast.type === 'success'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : synToast.type === 'error'
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'bg-blue-50 border-blue-200 text-blue-700'
            }`}
          >
            {synToast.message}
          </div>
        </div>
      )}

      {showMoveModal && selectedLaanebil && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black bg-opacity-50 overflow-y-auto p-4 sm:p-8">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg w-full max-w-2xl mx-auto p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Flyt lånebil - {selectedLaanebil.laanebil_internt_nummer || selectedLaanebil.internt_nummer}
            </h2>
            <div className="mb-4" />
            <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-4 mb-4 text-sm text-gray-700 dark:text-gray-200">
              <p><span className="font-medium">Station:</span> {selectedLaanebil.station_navn || 'Ikke angivet'}</p>
              <p><span className="font-medium">Erstatter:</span> {selectedLaanebil.erstattet_bil_internt_nummer || 'Ingen'}</p>
            </div>
            {supplementaryError && (
              <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
                {supplementaryError}
              </div>
            )}
            {supplementaryLoading ? (
              <div className="py-10 text-center text-gray-600 dark:text-gray-300">
                Henter data...
              </div>
            ) : (
              <form onSubmit={handleMoveSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Ny station</label>
                  <select
                    value={moveData.station_id}
                    onChange={(e) => {
                      const value = e.target.value;
                      setMoveData((prev) => ({ ...prev, station_id: value, workshop_car_id: '' }));
                      fetchWorkshopCarsForStation(value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                    required
                  >
                    <option value="">Vælg station</option>
                    {stationer.map((station) => (
                      <option key={station.id} value={station.id}>
                        {station.navn}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Vælg køretøj til værksted</label>
                  <select
                    value={moveData.workshop_car_id}
                    onChange={(e) => setMoveData((prev) => ({ ...prev, workshop_car_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                    required
                    disabled={!moveData.station_id || workshopLoading}
                  >
                    <option value="">{moveData.station_id ? (workshopLoading ? 'Henter køretøjer...' : 'Vælg køretøj') : 'Vælg station først'}</option>
                    {workshopCars.map((car) => (
                      <option key={car.id} value={car.id}>
                        {car.internt_nummer} - {car.vogn_type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Bemærkninger</label>
                  <textarea
                    value={moveData.bemaerkninger}
                    onChange={(e) => setMoveData((prev) => ({ ...prev, bemaerkninger: e.target.value }))}
                    rows={4}
                    placeholder="Evt. noter til flytningen..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Send kopi til (valgfrit)</label>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="w-full sm:flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                    >
                      <option value="">Vælg bruger</option>
                      {users.map((user) => (
                        <option key={user.id} value={String(user.id)}>
                          {(user.name || user.brugernavn)}{user.email ? ` (${user.email})` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        const idNum = Number(selectedUserId);
                        if (!idNum || selectedRecipients.includes(idNum)) return;
                        setSelectedRecipients((prev) => [...prev, idNum]);
                        setSelectedUserId('');
                      }}
                      className="w-full sm:w-auto px-4 py-2 bg-falck-blue text-white rounded-md hover:bg-blue-700"
                    >
                      Tilføj
                    </button>
                  </div>
                  {selectedRecipients.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedRecipients.map((id) => {
                        const user = users.find((u) => String(u.id) === String(id));
                        const label = user ? (user.name || user.brugernavn || `#${id}`) : `#${id}`;
                        return (
                          <span key={id} className="inline-flex items-center gap-2 px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700">
                            {label}
                            <button type="button" className="text-red-600" onClick={() => handleRemoveRecipient(id)}>
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Ekstra emails (kommasepareret)</label>
                  <input
                    type="text"
                    value={manualEmails}
                    onChange={(e) => setManualEmails(e.target.value)}
                    placeholder="fx: a@firma.dk, b@firma.dk"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
                {moveError && (
                  <div className="rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
                    {moveError}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleCloseMoveModal}
                    className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Annuller
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-falck-blue text-white rounded-md hover:bg-blue-700"
                  >
                    Flyt
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {showReturnModal && selectedLaanebil && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg w-full max-w-md sm:max-w-lg mx-auto p-4 sm:p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Returner lånebil - {selectedLaanebil.laanebil_internt_nummer || selectedLaanebil.internt_nummer}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Bekræft at lånebilen er returneret. Handling åbnes i nyt vindue.
            </p>
            {returnError && (
              <div className="rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
                {returnError}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={handleCloseReturnModal}
                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Annuller
              </button>
              <button
                onClick={handleReturnConfirm}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Returner lånebil
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;

// Enkel, adaptiv velkomstkomponent
const AdaptiveWelcome = ({ stats, udlaanteCount = 0, lowTypes = [] }) => {
  const hour = new Date().getHours();
  const greeting = hour < 5 ? 'God nat' : hour < 10 ? 'Godmorgen' : hour < 13 ? 'God formiddag' : hour < 18 ? 'God eftermiddag' : 'God aften';

  const highlights = [];
  if (Number(stats.aktiveFejlmeldinger) > 0) highlights.push(`${stats.aktiveFejlmeldinger} åbne fejlmeldinger`);
  if (udlaanteCount > 0) highlights.push(`${udlaanteCount} lånebil${udlaanteCount === 1 ? '' : 'er'} udlånt`);
  if (Number(stats.totalBiler) > 0) highlights.push(`${stats.totalBiler} biler i drift`);
  if (Number(stats.totalStationer) > 0) highlights.push(`${stats.totalStationer} stationer`);

  let msg = highlights.length > 0
    ? `Du har ${highlights.slice(0, 2).join(' og ')}.`
    : 'Ingen særlige hændelser lige nu.';
  if (Array.isArray(lowTypes) && lowTypes.length > 0) {
    const formatList = (arr) => {
      if (arr.length === 1) return arr[0];
      if (arr.length === 2) return `${arr[0]} og ${arr[1]}`;
      return `${arr.slice(0, -1).join(', ')} og ${arr[arr.length - 1]}`;
    };
    msg += ` Kun 1 tilbage af: ${formatList(lowTypes)}.`;
  }

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm p-6">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-1">{greeting}!</h1>
      <p className="text-gray-600 dark:text-gray-300">{msg}</p>
    </div>
  );
};

const ActiveServiceSessions = ({ sessions = [], onResume, onDelete }) => {
  const hasSessions = Array.isArray(sessions) && sessions.length > 0;
  if (!hasSessions) return null;

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm">
      <div className="p-6 border-b border-gray-200 dark:border-gray-800">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Aktive services (kladder)</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Lokale kladder gemmes i 7 dage på denne enhed.</p>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {sessions.map((session) => (
            <div
              key={session.key}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/40"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {session.interntNummer || (session.bilId ? `Bil ID ${session.bilId}` : 'Ukendt bil')}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-1 truncate">
                  {session.categoryNames?.length
                    ? session.categoryNames.join(', ')
                    : (session.categoryIds?.length
                        ? `Kategorier: ${session.categoryIds.join(', ')}`
                        : 'Ingen kategorier angivet')}
                </p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {session.kilometerstand && (
                    <span>Kilometerstand: {session.kilometerstand}</span>
                  )}
                  {session.mechanicSignature && (
                    <span>Mekaniker: {session.mechanicSignature}</span>
                  )}
                  <span>
                    Opdateret: {session.updatedAt ? new Date(session.updatedAt).toLocaleString('da-DK', { dateStyle: 'short', timeStyle: 'short' }) : 'Ukendt'}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => onResume && onResume(session)}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-falck-blue hover:bg-blue-700 rounded-md transition-colors"
                >
                  Fortsæt
                </button>
                <button
                  type="button"
                  onClick={() => onDelete && onDelete(session.key)}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                >
                  Fjern
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
